if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      // Serialize all add flows to avoid races across multiple product forms
      static __QUEUE__ = Promise.resolve();

      constructor() {
        super();

        // ---- Config ----------------------------------------------------------
        this.PROTECTION_VARIANT_ID = 46846832935164; // <-- update if changed

        // ---- Dawn wiring -----------------------------------------------------
        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        // Preflight snapshot (cart state BEFORE add) so we can detect:
        // - cart empty OR digital-only => first shippable item triggers protection
        this.__cartPreflight = null;

        this.submitButton?.addEventListener(
          'click',
          () => {
            fetch('/cart.js', { credentials: 'same-origin' })
              .then((r) => r.json())
              .then((c) => {
                const items = c.items || [];
                const hadProtection = items.some((it) => it.id === this.PROTECTION_VARIANT_ID);

                const hadShippable =
                  items.filter(
                    (it) =>
                      it.id !== this.PROTECTION_VARIANT_ID &&
                      it.requires_shipping === true
                  ).length > 0;

                this.__cartPreflight = {
                  hadProtection,
                  hadShippable,
                };
              })
              .catch(() => {
                this.__cartPreflight = { hadProtection: false, hadShippable: false };
              });
          },
          { capture: true }
        );
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

        // Build formdata for main product
        const cfg1 = fetchConfig('javascript');
        cfg1.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete cfg1.headers['Content-Type'];

        const fd1 = new FormData(this.form);

        if (this.cart) {
          fd1.append('sections', this.cart.getSectionsToRender().map((s) => s.id));
          fd1.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        cfg1.body = fd1;

        // Run everything inside a queue to avoid overlapping adds
        this.constructor.__QUEUE__ = this.constructor.__QUEUE__.then(async () => {
          let finalResponse;

          try {
            // 1) Add the main product
            const res1 = await fetch(`${routes.cart_add_url}`, cfg1);
            const data1 = await res1.json();

            if (data1.status) {
              this._showSoldOutError(data1.description);
              this.error = true;
              return;
            }

            finalResponse = data1;

            // 2) Decide whether to auto-add protection
            const pre = this.__cartPreflight || { hadProtection: false, hadShippable: false };

            const cartNow = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) =>
              r.json()
            );

            const alreadyHasProtection = (cartNow.items || []).some(
              (it) => it.id === this.PROTECTION_VARIANT_ID
            );

            const realItems = (cartNow.items || []).filter((it) => it.id !== this.PROTECTION_VARIANT_ID);
            const shippableRealItems = realItems.filter((it) => it.requires_shipping === true);

            const shouldAutoAddProtection =
              !pre.hadProtection &&         // didn’t already have it before click
              !pre.hadShippable &&          // no physical items before click (empty or digital-only)
              !alreadyHasProtection &&      // still not present now
              shippableRealItems.length === 1; // first physical item now exists

            if (shouldAutoAddProtection) {
              const cfg2 = fetchConfig('javascript');
              cfg2.headers['X-Requested-With'] = 'XMLHttpRequest';
              delete cfg2.headers['Content-Type'];

              const fd2 = new FormData();
              fd2.append('id', String(this.PROTECTION_VARIANT_ID));
              fd2.append('quantity', '1');

              if (this.cart) {
                fd2.append('sections', this.cart.getSectionsToRender().map((s) => s.id));
                fd2.append('sections_url', window.location.pathname);
              }

              cfg2.body = fd2;

              const res2 = await fetch(`${routes.cart_add_url}`, cfg2);
              const data2 = await res2.json();

              // If success, render with latest response (so drawer updates once)
              if (!data2.status) {
                finalResponse = data2;
              }
            }

            // 3) Render drawer/notification ONCE, with final state
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => this.cart.renderContents(finalResponse), 0);
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(finalResponse);
            }
          } catch (e) {
            console.error(e);
          } finally {
            // Reset preflight so subsequent adds don’t reuse old state
            this.__cartPreflight = null;

            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
          }
        });
      }

      _showSoldOutError(description) {
        this.handleErrorMessage(description);
        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        if (soldOutMessage) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.querySelector('span')?.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
        }
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }
    }
  );
}
